import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Body,
  Button,
  Card,
  I,
  IconTile,
  Label,
  Row,
  Stack,
} from '@leanin/ui';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { useFinishOnboarding } from '@/components/onboarding/finish-onboarding';
import { trackOnboardingStepViewed } from '@/lib/analytics/events';
import { journeyStepParams } from '@/lib/analytics/onboarding-steps';
import { readEntryJourney, type EntryJourney } from '@/lib/entry-journey';

// Ad-journey onboarding completion. The CTA runs the shared finish hook
// with the journey's persona and no explicit destination — the hook's own
// landing logic already routes each journey home (created-circle stash →
// the new Circle, join-circle → /circles, claimed join-link group → the
// Network, default → /feed). A direct hit with no journey in sessionStorage
// falls back to the organic flow.
export const Route = createFileRoute('/onboarding/j/youre-in')({
  beforeLoad: () => {
    const journey = readEntryJourney();
    if (journey === null) {
      throw redirect({ to: '/onboarding/profile' });
    }
    // The join journeys have no completion screen — their About step
    // finishes onboarding itself.
    if (journey === 'join-circle' || journey === 'join-network') {
      throw redirect({ to: '/onboarding/j/about' });
    }
    return { journey };
  },
  component: JourneyYoureInPage,
});

// The persona code each journey completes as (the finish hook takes the
// short codes, `personaForJourney` the long names — same mapping).
const JOURNEY_PERSONA_CODE: Record<EntryJourney, 'li' | 'cm' | 'cl'> = {
  individual: 'li',
  'start-circle': 'cl',
  'join-circle': 'cm',
  'join-network': 'li',
};

// "Where to start" rows — informational, deliberately not clickable: the
// single CTA below is the only action (per design review, the rows preview
// what's waiting rather than competing with the button).
const WHERE_TO_START = {
  individual: [
    { title: 'individual.item1Title', body: 'individual.item1Body', icon: I.book },
    { title: 'individual.item2Title', body: 'individual.item2Body', icon: I.users },
    { title: 'individual.item3Title', body: 'individual.item3Body', icon: I.cal },
    { title: 'individual.item4Title', body: 'individual.item4Body', icon: I.msg },
  ],
  'start-circle': [
    { title: 'start-circle.item1Title', body: 'start-circle.item1Body', icon: I.cal },
    { title: 'start-circle.item2Title', body: 'start-circle.item2Body', icon: I.edit },
    { title: 'start-circle.item3Title', body: 'start-circle.item3Body', icon: I.book },
  ],
  'join-circle': [
    { title: 'join-circle.item1Title', body: 'join-circle.item1Body', icon: I.search },
    { title: 'join-circle.item2Title', body: 'join-circle.item2Body', icon: I.users },
  ],
  'join-network': [
    { title: 'join-network.item1Title', body: 'join-network.item1Body', icon: I.cal },
    { title: 'join-network.item2Title', body: 'join-network.item2Body', icon: I.users },
  ],
} as const satisfies Record<
  EntryJourney,
  ReadonlyArray<{ title: string; body: string; icon: ReactNode }>
>;

function JourneyYoureInPage() {
  const t = useTranslations('EntryJourneys');
  const { journey } = Route.useRouteContext();
  const finishOnboarding = useFinishOnboarding(JOURNEY_PERSONA_CODE[journey]);
  const router = useRouter();
  const [finishing, setFinishing] = useState(false);

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackOnboardingStepViewed(
      journeyStepParams(journey, '/onboarding/j/youre-in'),
    );
  }, [journey]);

  const onFinish = () => {
    if (finishing) return;
    setFinishing(true);
    void finishOnboarding().finally(() => setFinishing(false));
  };

  // Headline only — no eyebrow, subtitle, or card header row; the rows +
  // CTA carry the step (only start-circle and individual reach this screen,
  // both on the pared layout).
  return (
    <EntryJourneyScreen
      journey={journey}
      current="youre-in"
      title={t(`onboarding.youreIn.${journey}.title`)}
    >
      <Card style={{ width: '100%', maxWidth: 440 }}>
        <Stack gap="lg">
          {WHERE_TO_START[journey].map((item) => (
            <Row key={item.title} gap="md" align="flex-start">
              <IconTile tone="burgundy">{item.icon}</IconTile>
              <Stack gap="xs">
                <Label>{t(`onboarding.youreIn.${item.title}`)}</Label>
                <Body size="sm" color="mute">
                  {t(`onboarding.youreIn.${item.body}`)}
                </Body>
              </Stack>
            </Row>
          ))}
          <Button
            variant="primary"
            iconRight={I.arrow}
            loading={finishing}
            onClick={onFinish}
            block
          >
            {t(`onboarding.youreIn.${journey}.cta`)}
          </Button>
          <Button
            variant="ghost"
            type="button"
            block
            onClick={() => router.history.back()}
          >
            {t('onboarding.youreIn.back')}
          </Button>
        </Stack>
      </Card>
    </EntryJourneyScreen>
  );
}
